import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Building2, TrendingUp, Users, Award } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function CCorporation() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Building2, title: 'Investor Friendly', description: 'Attract investors with flexible stock options and ownership structure.' },
    { icon: TrendingUp, title: 'Unlimited Growth', description: 'No restrictions on number of shareholders or stock classes.' },
    { icon: Users, title: 'Professional Image', description: 'Establish credibility with corporate structure and governance.' },
    { icon: Award, title: 'Perpetual Existence', description: 'Business continues regardless of ownership changes.' },
  ];

  const faqs = [
    { question: 'What is a C Corporation?', answer: 'A C Corporation is a legal structure where the business is a separate entity from its owners, providing liability protection and allowing for unlimited shareholders and multiple stock classes.' },
    { question: 'What are the tax implications of a C Corp?', answer: 'C Corporations face double taxation – the corporation pays taxes on profits, and shareholders pay taxes on dividends. However, they also benefit from certain deductions and employee benefit options.' },
    { question: 'Who should form a C Corporation?', answer: 'C Corps are ideal for businesses planning to seek venture capital, go public, or need the flexibility of multiple stock classes and unlimited shareholders.' },
    { question: 'What are the compliance requirements?', answer: 'C Corps must hold annual shareholder meetings, maintain detailed records, file annual reports, and follow corporate formalities including bylaws and board resolutions.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>C Corporation Formation</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Form your C Corporation and unlock growth potential with unlimited shareholders.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Form a C Corporation?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Build a scalable business structure for long-term growth.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about C Corporation formation.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Ready to Incorporate Your Business?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Start your C Corporation today and position your business for growth.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="c_corporation" />
      <Footer />
    </div>
  );
}